import { Thermometer, Snowflake } from "lucide-react";

/**
 * HeatGradientDiagram
 * ----------------------------------------
 * SVG cross-section of a vivarium showing the heat gradient with real
 * heating equipment placed inside: dome basking lamp + UVB tube (bulb mode),
 * under-tank heat mat (mat mode), or ambient-only. Hides, water bowl and
 * substrate are included so keepers can see correct hot-end / cool-end layout.
 *
 * Props (all optional strings):
 *   heatSource: "bulb" | "mat" | "none"  (default: "bulb")
 *   basking, warmHide, ambient, night    (display-ready strings like "34-36")
 *   uvi                                  (e.g. "4.0-4.5 at basking")
 */
const HeatGradientDiagram = ({
  heatSource = "bulb",
  basking,
  warmHide,
  ambient,
  night,
  uvi,
}) => {
  return (
    <div
      className="bg-[#121212] border border-white/10 p-6"
      data-testid="guide-heat-gradient"
    >
      <div className="flex items-center gap-2 mb-4">
        <Thermometer className="w-5 h-5 text-amber-400" />
        <h2 className="text-lg font-bold text-[#F4F1DE]">
          Enclosure heat gradient &amp; equipment layout
        </h2>
      </div>

      <p className="text-xs text-[#F4F1DE]/50 mb-5 leading-relaxed">
        Heat sources sit at ONE end so your reptile can thermoregulate.
        Never heat both ends — the animal needs a cool zone to retreat to.
      </p>

      <EnclosureSVG
        heatSource={heatSource}
        basking={basking}
        warmHide={warmHide}
        ambient={ambient}
      />

      {/* Legend */}
      <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
        <LegendItem color="bg-red-400" label="Basking" value={basking ? `${basking}°C` : "—"} />
        <LegendItem
          color="bg-orange-400"
          label="Warm hide"
          value={
            warmHide && warmHide !== "not applicable — use natural gradient"
              ? `${warmHide}°C`
              : "—"
          }
        />
        <LegendItem color="bg-yellow-400" label="Ambient" value={ambient ? `${ambient}°C` : "—"} />
        <LegendItem
          color="bg-blue-400"
          label="Night min"
          value={night ? `${night}°C` : "—"}
          icon={Snowflake}
        />
      </div>

      {uvi && (
        <p
          className="mt-4 text-xs text-[#F4F1DE]/60 leading-relaxed"
          data-testid="guide-heat-uvi-note"
        >
          <span className="text-yellow-400 font-semibold">UVB:</span> aim for {uvi}. Mount the UVB
          tube on the same side as the basking lamp so the animal gets heat + UV together.
        </p>
      )}
    </div>
  );
};

/* -------------------- SVG vivarium cross-section -------------------- */

const EnclosureSVG = ({ heatSource, basking, warmHide, ambient }) => {
  // Canvas
  const W = 800;
  const H = 340;

  // Enclosure interior
  const ix = 40;
  const iy = 40;
  const iw = W - ix * 2;
  const ih = H - 80;

  return (
    <div className="w-full overflow-x-auto">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="w-full h-auto"
        role="img"
        aria-label="Vivarium cross-section showing heat gradient and equipment placement"
      >
        <defs>
          {/* Gradient inside the enclosure */}
          <linearGradient id="heatGrad" x1="0" y1="0" x2="1" y2="0">
            {heatSource === "none" ? (
              <>
                <stop offset="0%" stopColor="rgba(96,165,250,0.35)" />
                <stop offset="100%" stopColor="rgba(96,165,250,0.18)" />
              </>
            ) : heatSource === "mat" ? (
              <>
                <stop offset="0%" stopColor="rgba(251,146,60,0.55)" />
                <stop offset="50%" stopColor="rgba(251,191,36,0.3)" />
                <stop offset="100%" stopColor="rgba(96,165,250,0.3)" />
              </>
            ) : (
              <>
                <stop offset="0%" stopColor="rgba(239,68,68,0.62)" />
                <stop offset="35%" stopColor="rgba(251,146,60,0.45)" />
                <stop offset="65%" stopColor="rgba(251,191,36,0.3)" />
                <stop offset="100%" stopColor="rgba(96,165,250,0.32)" />
              </>
            )}
          </linearGradient>

          {/* Light cone gradient */}
          <linearGradient id="lightCone" x1="0.5" y1="0" x2="0.5" y2="1">
            <stop offset="0%" stopColor="rgba(253,224,71,0.55)" />
            <stop offset="100%" stopColor="rgba(253,224,71,0)" />
          </linearGradient>

          {/* Wooden frame */}
          <linearGradient id="wood" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#4b3422" />
            <stop offset="100%" stopColor="#2a1d12" />
          </linearGradient>

          {/* Substrate */}
          <linearGradient id="substrate" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#8b6b4a" />
            <stop offset="100%" stopColor="#5a4530" />
          </linearGradient>
        </defs>

        {/* Outer wooden vivarium frame */}
        <rect x="20" y="20" width={W - 40} height={H - 40} fill="url(#wood)" rx="4" />
        {/* Interior background gradient */}
        <rect x={ix} y={iy} width={iw} height={ih} fill="url(#heatGrad)" />
        {/* Glass reflection sheen on top */}
        <rect x={ix} y={iy} width={iw} height={ih * 0.25} fill="rgba(255,255,255,0.04)" />

        {/* Hot / Cool side labels */}
        <text
          x={ix + 16}
          y={iy + 22}
          fontSize="11"
          fontWeight="700"
          fill="#fca5a5"
          style={{ letterSpacing: "0.15em" }}
        >
          HOT SIDE
        </text>
        <text
          x={ix + iw - 16}
          y={iy + 22}
          fontSize="11"
          fontWeight="700"
          fill="#93c5fd"
          textAnchor="end"
          style={{ letterSpacing: "0.15em" }}
        >
          COOL SIDE
        </text>

        {/* UVB tube across the ceiling (only if bulb mode) */}
        {heatSource === "bulb" && (
          <g>
            <rect x={ix + 30} y={iy + 34} width={iw - 60} height="8" fill="#1f2937" rx="2" />
            <rect
              x={ix + 34}
              y={iy + 36}
              width={iw - 68}
              height="4"
              fill="#a78bfa"
              opacity="0.8"
            />
            <text
              x={ix + iw / 2}
              y={iy + 58}
              fontSize="9"
              fill="#c4b5fd"
              textAnchor="middle"
              style={{ letterSpacing: "0.2em" }}
            >
              UVB T5 TUBE
            </text>
          </g>
        )}

        {/* Basking dome lamp on hot side */}
        {heatSource === "bulb" && (
          <g transform={`translate(${ix + 90}, ${iy + 68})`}>
            {/* Bracket */}
            <rect x="-3" y="-24" width="6" height="24" fill="#3f3f46" />
            {/* Dome */}
            <path
              d="M -28 0 L 28 0 L 22 22 L -22 22 Z"
              fill="#9ca3af"
              stroke="#1f2937"
              strokeWidth="1"
            />
            <rect x="-30" y="-2" width="60" height="4" fill="#4b5563" />
            {/* Bulb filament glow */}
            <ellipse cx="0" cy="20" rx="11" ry="6" fill="#fde047" />
            <ellipse cx="0" cy="20" rx="6" ry="3" fill="#fef9c3" />
            {/* Light cone */}
            <polygon points="-22,22 22,22 70,160 -70,160" fill="url(#lightCone)" />
            {/* Label */}
            <text x="0" y="-32" fontSize="9" fill="#fde047" textAnchor="middle" style={{ letterSpacing: "0.2em" }}>
              BASKING LAMP
            </text>
          </g>
        )}

        {/* Heat mat on hot side (mat mode) */}
        {heatSource === "mat" && (
          <g>
            <rect
              x={ix + 40}
              y={iy + ih - 34}
              width={iw * 0.3}
              height="10"
              fill="#fb923c"
              stroke="#9a3412"
              strokeWidth="1"
            />
            {/* Heat lines */}
            {[0, 1, 2, 3, 4, 5, 6].map((i) => (
              <line
                key={i}
                x1={ix + 60 + i * 24}
                y1={iy + ih - 34}
                x2={ix + 60 + i * 24}
                y2={iy + ih - 48}
                stroke="#fb923c"
                strokeWidth="2"
                opacity="0.6"
              />
            ))}
            <text
              x={ix + 40 + (iw * 0.3) / 2}
              y={iy + ih - 12}
              fontSize="9"
              fill="#fb923c"
              textAnchor="middle"
              style={{ letterSpacing: "0.2em" }}
            >
              UNDER-TANK HEAT MAT
            </text>
          </g>
        )}

        {/* No-heat mode: ambient bulb note */}
        {heatSource === "none" && (
          <g>
            <rect x={ix + 30} y={iy + 34} width={iw - 60} height="8" fill="#1f2937" rx="2" />
            <rect x={ix + 34} y={iy + 36} width={iw - 68} height="4" fill="#a78bfa" opacity="0.8" />
            <text
              x={ix + iw / 2}
              y={iy + 58}
              fontSize="9"
              fill="#c4b5fd"
              textAnchor="middle"
              style={{ letterSpacing: "0.2em" }}
            >
              LOW-LEVEL UVB TUBE (AMBIENT ONLY — NO BASKING BULB)
            </text>
          </g>
        )}

        {/* Substrate along the floor */}
        <rect
          x={ix}
          y={iy + ih - 28}
          width={iw}
          height="28"
          fill="url(#substrate)"
        />
        {/* Substrate texture dots */}
        {Array.from({ length: 28 }).map((_, i) => (
          <circle
            key={i}
            cx={ix + 8 + (i * (iw - 16)) / 27}
            cy={iy + ih - 10 + ((i * 7) % 6) - 2}
            r="1.2"
            fill="#3d2a18"
            opacity="0.8"
          />
        ))}

        {/* Basking rock (hot side) */}
        {heatSource !== "none" && (
          <g>
            <ellipse cx={ix + 90} cy={iy + ih - 28} rx="44" ry="18" fill="#6b5240" />
            <ellipse cx={ix + 90} cy={iy + ih - 30} rx="40" ry="12" fill="#8a6b52" />
          </g>
        )}

        {/* Warm hide (middle-warm) */}
        <g>
          <path
            d={`M ${ix + iw * 0.32} ${iy + ih - 28}
                Q ${ix + iw * 0.38} ${iy + ih - 68},
                  ${ix + iw * 0.47} ${iy + ih - 28} Z`}
            fill="#4a3829"
            stroke="#2a1d12"
            strokeWidth="1.5"
          />
          <rect
            x={ix + iw * 0.385}
            y={iy + ih - 48}
            width="18"
            height="20"
            fill="#0a0a0a"
            rx="2"
          />
        </g>

        {/* Water bowl (cool side) */}
        <g>
          <ellipse cx={ix + iw * 0.72} cy={iy + ih - 26} rx="28" ry="8" fill="#374151" />
          <ellipse cx={ix + iw * 0.72} cy={iy + ih - 28} rx="24" ry="5" fill="#3b82f6" opacity="0.7" />
          <text x={ix + iw * 0.72} y={iy + ih - 10} fontSize="8" fill="#9ca3af" textAnchor="middle">
            water
          </text>
        </g>

        {/* Cool hide (far right) */}
        <g>
          <path
            d={`M ${ix + iw * 0.86} ${iy + ih - 28}
                Q ${ix + iw * 0.91} ${iy + ih - 62},
                  ${ix + iw * 0.97} ${iy + ih - 28} Z`}
            fill="#3a4a5a"
            stroke="#1e293b"
            strokeWidth="1.5"
          />
          <rect
            x={ix + iw * 0.905}
            y={iy + ih - 48}
            width="16"
            height="20"
            fill="#0a0a0a"
            rx="2"
          />
        </g>

        {/* Temperature pins pointing to zones */}
        {basking && (
          <TempPinSVG
            x={ix + 90}
            y={iy + ih - 60}
            label="Basking"
            value={`${basking} °C`}
            color="#fca5a5"
          />
        )}
        {warmHide && warmHide !== "not applicable — use natural gradient" && (
          <TempPinSVG
            x={ix + iw * 0.405}
            y={iy + ih - 86}
            label="Warm hide"
            value={`${warmHide} °C`}
            color="#fdba74"
          />
        )}
        {ambient && (
          <TempPinSVG
            x={ix + iw * 0.78}
            y={iy + ih - 82}
            label="Ambient"
            value={`${ambient} °C`}
            color="#93c5fd"
          />
        )}

        {/* Enclosure floor label */}
        <text
          x={ix + iw / 2}
          y={H - 8}
          fontSize="9"
          fill="#F4F1DE"
          opacity="0.45"
          textAnchor="middle"
          style={{ letterSpacing: "0.3em" }}
        >
          BIOACTIVE SUBSTRATE
        </text>
      </svg>
    </div>
  );
};

const TempPinSVG = ({ x, y, label, value, color }) => {
  const w = 88;
  const h = 34;
  return (
    <g transform={`translate(${x - w / 2}, ${y - h})`}>
      <rect
        x="0"
        y="0"
        width={w}
        height={h}
        fill="rgba(10,10,10,0.85)"
        stroke={color}
        strokeWidth="1"
        rx="3"
      />
      <text
        x={w / 2}
        y="13"
        fontSize="8"
        fill={color}
        textAnchor="middle"
        opacity="0.8"
        style={{ letterSpacing: "0.2em" }}
      >
        {label.toUpperCase()}
      </text>
      <text
        x={w / 2}
        y="27"
        fontSize="11"
        fontWeight="700"
        fill="#F4F1DE"
        textAnchor="middle"
      >
        {value}
      </text>
      {/* Pin stem down to zone */}
      <line
        x1={w / 2}
        y1={h}
        x2={w / 2}
        y2={h + 8}
        stroke={color}
        strokeWidth="1"
        strokeDasharray="2 2"
      />
      <circle cx={w / 2} cy={h + 10} r="2.5" fill={color} />
    </g>
  );
};

const LegendItem = ({ color, label, value, icon: Icon }) => (
  <div className="flex items-center gap-2 text-[#F4F1DE]/70">
    <span className={`w-2.5 h-2.5 ${color}`} />
    <div className="leading-tight">
      <div className="text-[9px] uppercase tracking-widest opacity-60 flex items-center gap-1">
        {Icon && <Icon className="w-2.5 h-2.5" />}
        {label}
      </div>
      <div className="text-[#F4F1DE] font-semibold">{value}</div>
    </div>
  </div>
);

export default HeatGradientDiagram;
